import { useState, useEffect, useRef, useMemo } from "react";
import type { RefObject } from "react";
import type Lenis from "lenis";
import { useParams } from "react-router";
import { useI18n } from "~/i18n";
import { resolveIpAssets } from "~/theme/resolve-theme";
import type { IpAssets } from "~/theme/types";

type Visual = IpAssets["heroVisuals"][number];

interface HeroSliderProps {
  visuals?: Visual[];
  lenisRef?: RefObject<Lenis | null>;
}

const SPARKLE_POSITIONS = [
  { top: "12%", left: "3%", duration: "5.2s", delay: "0s" },
  { top: "28%", left: "18%", duration: "6.8s", delay: "-1.5s" },
  { top: "8%", left: "33%", duration: "4.8s", delay: "-0.8s" },
  { top: "45%", left: "48%", duration: "7.2s", delay: "-3.0s" },
  { top: "20%", left: "62%", duration: "5.6s", delay: "-2.1s" },
  { top: "60%", left: "75%", duration: "6.4s", delay: "-1.2s" },
  { top: "15%", left: "82%", duration: "4.5s", delay: "-0.4s" },
  { top: "38%", left: "10%", duration: "7.8s", delay: "-2.8s" },
  { top: "70%", left: "55%", duration: "5.0s", delay: "-0.9s" },
  { top: "80%", left: "38%", duration: "6.1s", delay: "-4.0s" },
  { top: "55%", left: "70%", duration: "4.9s", delay: "-1.8s" },
  { top: "5%", left: "88%", duration: "5.7s", delay: "-0.5s" },
  { top: "85%", left: "25%", duration: "6.6s", delay: "-3.5s" },
  { top: "32%", left: "44%", duration: "4.3s", delay: "-1.1s" },
  { top: "72%", left: "85%", duration: "7.0s", delay: "-2.4s" },
  { top: "48%", left: "58%", duration: "5.4s", delay: "-0.7s" },
];

export function HeroSlider({ visuals, lenisRef }: HeroSliderProps) {
  const { t, locale } = useI18n();
  const { slug = "" } = useParams<{ slug: string }>();
  const assets = resolveIpAssets(slug);
  const resolvedVisuals = visuals ?? assets.heroVisuals;
  const [active, setActive] = useState(0);
  const innerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const lastSnapTimeRef = useRef(0);
  const scrollEndTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const pvTopRef = useRef<number | null>(null);
  const prevScrollYRef = useRef(0);

  const petals = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        id: i,
        left: Math.random() * 110 - 5,
        size: 10,
        duration: 9 + Math.random() * 8,
        delay: -(Math.random() * 10),
        driftX: (Math.random() > 0.5 ? 1 : -1) * (60 + Math.random() * 100),
        rotate: 180 + Math.random() * 360,
      })),
    [],
  );

  useEffect(() => {
    const cachePvTop = () => {
      const pvSection = document.getElementById("pv");
      const header = document.querySelector(".header");
      const headerHeight = header instanceof HTMLElement ? header.offsetHeight : 80;
      if (pvSection) pvTopRef.current = pvSection.offsetTop - headerHeight;
    };
    cachePvTop();
    window.addEventListener("resize", cachePvTop);

    const snapTo = (target: number) => {
      if (isScrollingRef.current) return;
      isScrollingRef.current = true;

      const lenis = lenisRef?.current;
      if (lenis) {
        lenis.scrollTo(target, {
          duration: 1,
          easing: (t: number) => 1 - Math.pow(1 - t, 3),
          force: true,
          lock: true,
          onComplete: () => {
            isScrollingRef.current = false;
            if (target === 0) lastSnapTimeRef.current = performance.now();
          },
        });
      } else {
        window.scrollTo({ behavior: "smooth", left: 0, top: target });
        isScrollingRef.current = false;
        if (target === 0) lastSnapTimeRef.current = performance.now();
      }
    };

    // nav 앵커 클릭 또는 hash 네비게이션 시 스냅 비활성화
    let navLockUntil = 0;
    // 페이지 로드 시 hash가 있으면 스냅 잠금 (서브 페이지에서 복귀 시)
    if (window.location.hash && window.location.hash.length > 1) {
      navLockUntil = performance.now() + 3000;
    }
    const onAnchorClick = (e: MouseEvent) => {
      const anchor = (e.target as Element)?.closest?.("a[href^='#']");
      if (anchor) navLockUntil = performance.now() + 2000;
    };
    document.addEventListener("click", onAnchorClick, { capture: true });

    const isNavLocked = () => performance.now() < navLockUntil;
    const isMobile = () => window.matchMedia("(max-width: 767px)").matches;

    const onScroll = () => {
      const scrollY = window.scrollY;
      const vh = window.innerHeight;
      const progress = Math.min(scrollY / (vh * 0.3), 1);
      if (innerRef.current) {
        if (isMobile()) {
          innerRef.current.style.filter = "";
          innerRef.current.style.transform = "";
          innerRef.current.style.opacity = "";
        } else {
          innerRef.current.style.filter = `blur(${progress * 6}px)`;
          innerRef.current.style.transform = `scale(${1 + progress * 0.02})`;
          innerRef.current.style.opacity = String(1 - progress * 0.2);
        }
      }

      const pvTop = pvTopRef.current;
      const prevY = prevScrollYRef.current;
      prevScrollYRef.current = scrollY;

      if (
        !isScrollingRef.current &&
        !isNavLocked() &&
        !isMobile() &&
        pvTop &&
        scrollY > 0 &&
        scrollY < pvTop
      ) {
        const goingDown = scrollY > prevY;
        const goingUp = scrollY < prevY;

        if (prevY >= pvTop && goingUp) {
          clearTimeout(scrollEndTimerRef.current);
          snapTo(0);
        } else if (prevY <= 5 && goingDown) {
          clearTimeout(scrollEndTimerRef.current);
          lastSnapTimeRef.current = performance.now();
          snapTo(pvTop);
        } else {
          clearTimeout(scrollEndTimerRef.current);
          const snapTarget = goingUp ? 0 : pvTop;
          scrollEndTimerRef.current = setTimeout(() => {
            if (isNavLocked()) return;
            const current = window.scrollY;
            if (current > 0 && pvTop && current < pvTop) {
              if (snapTarget === pvTop) lastSnapTimeRef.current = performance.now();
              snapTo(snapTarget);
            }
          }, 100);
        }
      }
    };

    const onWheel = (event: WheelEvent) => {
      if (isScrollingRef.current || isNavLocked() || isMobile()) return;
      const pvTop = pvTopRef.current;
      if (!pvTop) return;

      const isTrackpad = event.deltaMode === 0 && Math.abs(event.deltaY) < 40;
      const scrollY = window.scrollY;
      const atHero = scrollY < 10;
      const snapUpZone = scrollY > 0 && scrollY < pvTop;

      if (atHero && event.deltaY > 0) {
        event.stopImmediatePropagation();
        lastSnapTimeRef.current = performance.now();
        snapTo(pvTop);
        return;
      }

      if (snapUpZone && event.deltaY < 0) {
        if (isTrackpad) return;
        const timeSince = performance.now() - lastSnapTimeRef.current;
        const intentional = timeSince > 400 || event.deltaY < -25;
        if (intentional) {
          event.stopImmediatePropagation();
          snapTo(0);
        }
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("wheel", onWheel, { capture: true, passive: false });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("wheel", onWheel, { capture: true });
      document.removeEventListener("click", onAnchorClick, { capture: true });
      window.removeEventListener("resize", cachePvTop);
    };
  }, []);

  return (
    <section
      id="top"
      className="sticky top-0 z-0 -mt-20 h-dvh min-h-[640px] max-md:relative max-md:top-auto max-md:h-svh max-md:min-h-0 w-full bg-[var(--ip-surface)] md:mb-20"
    >
      <div
        ref={innerRef}
        className="relative w-full h-full flex items-center justify-center overflow-hidden will-change-[filter,transform,opacity] origin-center transition-[filter,opacity,transform] duration-[250ms] ease-out"
      >
        {/* Background layers */}
        {resolvedVisuals.map((v, i) => (
          <div
            key={v.id}
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-[800ms] ${i === active ? "opacity-100" : "opacity-0"} ${v.id === 2 ? "blur-[1px] brightness-[1.08] saturate-[1.2] scale-[1.02]" : ""}`}
            style={{ backgroundImage: `url(${v.bg})` }}
          />
        ))}

        {/* KV2 dark overlay */}
        <div
          className={`absolute inset-0 bg-[rgba(135,206,235,0.08)] z-[3] pointer-events-none transition-opacity duration-[800ms] ${active === 1 ? "opacity-100" : "opacity-0"}`}
        />

        {/* Glow orbs KV1 */}
        <div
          className={`absolute inset-0 z-[3] pointer-events-none transition-opacity duration-[800ms] ${active === 1 ? "opacity-0" : "opacity-100"}`}
        >
          <span className="absolute rounded-full blur-[60px] w-[680px] h-[680px] bg-[rgba(var(--ip-primary-glow),0.35)] -top-[20%] -left-[10%] animate-[orbFloat1_11s_ease-in-out_infinite]" />
          <span className="absolute rounded-full blur-[60px] w-[480px] h-[480px] bg-[rgba(141,208,232,0.4)] top-[5%] right-0 animate-[orbFloat2_8s_ease-in-out_-2s_infinite]" />
          <span className="absolute rounded-full blur-[60px] w-[400px] h-[400px] bg-[rgba(var(--ip-primary-glow),0.3)] bottom-[10%] left-[5%] animate-[orbFloat3_13s_ease-in-out_-5s_infinite]" />
          <span className="absolute rounded-full blur-[60px] w-[300px] h-[300px] bg-[rgba(180,230,245,0.5)] top-[30%] left-[40%] animate-[orbFloat4_7s_ease-in-out_-1s_infinite]" />
        </div>

        {/* Character layers */}
        {resolvedVisuals.map((v, i) =>
          v.char ? (
            <div
              key={`char-${v.id}`}
              className={`absolute inset-0 bg-cover bg-center bg-no-repeat z-[4] transition-opacity duration-[800ms] ${i === active ? "opacity-100 animate-[charZoomIn_1.8s_cubic-bezier(0.25,0.46,0.45,0.94)_forwards]" : "opacity-0"}`}
              style={{ backgroundImage: `url(${v.char})` }}
            />
          ) : null,
        )}

        {/* Halftone overlay */}
        <div className="absolute inset-0 z-[5] pointer-events-none bg-[radial-gradient(circle,rgba(255,255,255,0.2)_1px,transparent_1px)] bg-[length:4px_4px]" />

        {/* Gradient overlay */}
        <div
          className={`absolute inset-0 z-[6] transition-all duration-[800ms] ${active === 1 ? "bg-[linear-gradient(to_bottom,rgba(247,251,253,0.02)_0%,rgba(247,251,253,0)_50%,rgba(247,251,253,0.2)_80%,rgba(247,251,253,0.45)_100%)]" : "bg-[linear-gradient(to_bottom,rgba(247,251,253,0.05)_0%,rgba(247,251,253,0)_40%,rgba(247,251,253,0.6)_80%,rgba(247,251,253,0.9)_100%)]"}`}
        />

        {/* Sparkles (KV1) */}
        <div
          className={`absolute inset-0 z-[6] pointer-events-none overflow-hidden transition-opacity duration-[800ms] ${active === 0 ? "opacity-100" : "opacity-0"}`}
        >
          {SPARKLE_POSITIONS.map((s, i) => (
            <span
              key={i}
              className="hero-sparkle"
              style={{
                top: s.top,
                left: s.left,
                animationDuration: s.duration,
                animationDelay: s.delay,
              }}
            />
          ))}
        </div>

        {/* Petals (KV2) */}
        <div
          className={`absolute inset-0 z-[6] pointer-events-none overflow-hidden transition-opacity duration-[800ms] ${active === 1 ? "opacity-100" : "opacity-0"}`}
        >
          {petals.map((p) => (
            <span
              key={p.id}
              className="hero-petal"
              style={
                {
                  left: `${p.left}%`,
                  width: `${p.size}px`,
                  height: `${p.size * 1.3}px`,
                  animationDuration: `${p.duration}s`,
                  animationDelay: `${p.delay}s`,
                  "--drift-x": `${p.driftX}px`,
                  "--rotate": `${p.rotate}deg`,
                } as React.CSSProperties
              }
            />
          ))}
        </div>

        {/* Bottom content: logo + KV thumbnails */}
        <div className="hero__bottom absolute bottom-12 left-1/2 z-[7] flex w-full -translate-x-1/2 flex-col items-center gap-8 max-md:bottom-10 max-md:gap-4">
          <div className="hero__content px-6 text-center">
            <h1 className="hero__title mb-7 max-md:mb-3 flex flex-col items-center justify-center gap-1.5">
              <img
                src={assets.logos[locale as keyof typeof assets.logos] ?? assets.logos.ko}
                alt={assets.logoAlt[locale as keyof typeof assets.logoAlt] ?? assets.logoAlt.ko}
                className="hero__title-logo w-auto h-auto transition-[filter] duration-[800ms]"
                style={{
                  filter: "drop-shadow(0 0 8px white) drop-shadow(0 0 16px white)",
                  maxHeight: "clamp(90px, min(13vw, 15vh), 150px)",
                  maxWidth: "min(88vw, 620px)",
                }}
              />
            </h1>
          </div>

          <div className="hero__thumbnails flex gap-3">
            {resolvedVisuals.map((v, i) => (
              <button
                key={v.id}
                className={`hero__thumb flex flex-col items-center gap-1.5 border-0 bg-transparent p-0 text-[13.3px] leading-normal transition-opacity ${i === active ? "hero__thumb--active opacity-100" : "opacity-65 hover:opacity-85"}`}
                onClick={() => setActive(i)}
                aria-label={v.label}
              >
                <div
                  className={`hero__thumb-preview h-[clamp(80px,min(12vw,14vh),128px)] w-[clamp(60px,min(9vw,10vh),96px)] rounded border-2 bg-cover bg-center bg-origin-border transition-colors ${i === active ? "border-[var(--ip-primary)]" : "border-transparent"}`}
                  style={{ backgroundImage: `url(${v.thumb})` }}
                />
                <span
                  className={`hero__thumb-label text-[8px] tracking-[0.15em] text-[var(--ip-text-muted)]`}
                >
                  {v.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="hero__scroll absolute right-12 bottom-12 z-[8] flex flex-col items-center gap-2.5 max-md:hidden">
          <span
            className={`hero__scroll-text text-[13px] tracking-[0.35em] [writing-mode:vertical-rl] text-[var(--ip-text-muted)]`}
          >
            {t.hero.scrollDown}
          </span>
          <div
            className={`hero__scroll-line h-24 w-0.5 animate-[scrollLine_2s_ease_infinite] bg-gradient-to-b from-[var(--ip-primary)] to-transparent`}
          />
        </div>
      </div>
    </section>
  );
}
